import { useMutation } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Controller, useForm } from "react-hook-form";

import { TeamOnboardingSurvey } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import FormLabel from "~/components/login/formLabel/FormLabel";
import FormTitle from "~/components/login/formTitle/FormTitle";
import Select from "~/components/mantine/select/Select";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import ProgressBar from "~/components/ui/progressBar/ProgressBar";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { HUBSPOT_TOKEN_COOKIE_KEY } from "~/helpers/consts";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import loginStyles from "~/pages/login/Login.module.scss";

import styles from "./Onboarding.module.scss";

type Option<T extends string> = { value: T; label: string };

// The backend stores only the keys; the labels live here.
const AEO_SEO_SERVICES: Option<TeamOnboardingSurvey["aeo_seo_services"]>[] = [
  { value: "not_yet", label: "Not yet, but I want to" },
  { value: "add_on", label: "As an add-on" },
  { value: "core_service", label: "As a core service" },
];

const ACTIVE_CLIENTS: Option<TeamOnboardingSurvey["active_clients"]>[] = [
  { value: "1_5", label: "1–5" },
  { value: "6_20", label: "6–20" },
  { value: "21_50", label: "21–50" },
  { value: "50_plus", label: "50+" },
];

const MONTHLY_RETAINER: Option<TeamOnboardingSurvey["monthly_retainer"]>[] = [
  { value: "under_1k", label: "Under $1k" },
  { value: "1k_3k", label: "$1–3k" },
  { value: "3k_10k", label: "$3–10k" },
  { value: "10k_plus", label: "$10k+" },
];

const LAUNCH_TIMELINE: Option<TeamOnboardingSurvey["launch_timeline"]>[] = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "this_month", label: "This month" },
  { value: "not_sure", label: "Not sure" },
];

type FormValues = {
  [Key in keyof TeamOnboardingSurvey]: TeamOnboardingSurvey[Key] | null;
};

// Each question takes only its own options.
type Question = {
  [Key in keyof FormValues]: {
    name: Key;
    label: string;
    options: Option<NonNullable<FormValues[Key]>>[];
  };
}[keyof FormValues];

// In the order they're asked.
const QUESTIONS: Question[] = [
  {
    name: "aeo_seo_services",
    label: "Do you currently sell AEO and SEO services?",
    options: AEO_SEO_SERVICES,
  },
  {
    name: "active_clients",
    label: "How many active clients do you have?",
    options: ACTIVE_CLIENTS,
  },
  {
    name: "monthly_retainer",
    label: "What's your average monthly retainer per client?",
    options: MONTHLY_RETAINER,
  },
  {
    name: "launch_timeline",
    label:
      "In a perfect world, how soon would you like to launch your platform?",
    options: LAUNCH_TIMELINE,
  },
];

const OnboardingSurveyPage = () => {
  usePageTitle(PAGE_TITLES.platformBeingBuilt);

  const { team, refetchTeam } = useTeam();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      aeo_seo_services: null,
      active_clients: null,
      monthly_retainer: null,
      launch_timeline: null,
    },
  });

  // The backend reports survey errors under the `onboarding_survey` key.
  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors<FormValues>(setError);

  const { mutateAsync } = useMutation({
    mutationFn: (onboardingSurvey: TeamOnboardingSurvey) => {
      const hutk = Cookies.get(HUBSPOT_TOKEN_COOKIE_KEY);

      return apiClient.teams.teamsPartialUpdate(team?.id ?? 0, {
        onboarding_survey: onboardingSurvey,
        hubspot_context: {
          page_uri: window.location.origin + window.location.pathname,
          page_name: document.title,
        },
        ...(hutk ? { hutk } : {}),
      });
    },
  });

  const answeredCount = watch(QUESTIONS.map(({ name }) => name)).filter(
    Boolean,
  ).length;
  const isTailored = answeredCount === QUESTIONS.length;

  // The platform itself was set up on the previous step; the answers are
  // what's left to tailor it, so the last step follows the questions.
  const buildSteps = [
    { label: "Platform name and logo saved", isDone: true },
    {
      label: isTailored
        ? "Tailored to your agency"
        : "Tailoring it to your agency…",
      isDone: isTailored,
    },
  ];

  const onSubmit = async (values: FormValues) => {
    clearBaseErrors();

    const {
      aeo_seo_services,
      active_clients,
      monthly_retainer,
      launch_timeline,
    } = values;

    // Guaranteed by the `required` rules below.
    if (
      !aeo_seo_services ||
      !active_clients ||
      !monthly_retainer ||
      !launch_timeline
    )
      return;

    const result = await tryCatchParsed(
      mutateAsync({
        aeo_seo_services,
        active_clients,
        monthly_retainer,
        launch_timeline,
      }),
    );

    if (result.error) {
      const surveyErrors = result.error.validations?.onboarding_survey;

      applyServerErrors(
        surveyErrors
          ? { ...result.error, validations: { base: surveyErrors } }
          : result.error,
      );

      return;
    }

    // Clears onboarding_survey_required, so the guard moves on to the app.
    await refetchTeam();
  };

  const requiredRule = { required: labels.THIS_FIELD_CANNOT_BE_EMPTY };

  return (
    <>
      <FormTitle title={PAGE_TITLES.platformBeingBuilt} />
      <div className={loginStyles.infoText}>
        Please answer four quick questions so we can tailor it to your agency.
      </div>
      <div className={styles.progress}>
        <ProgressBar
          min={answeredCount}
          max={QUESTIONS.length}
          customText={`${answeredCount} of ${QUESTIONS.length} answered`}
        />
      </div>
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        {QUESTIONS.map(({ name, label, options }) => (
          <div key={name} className={loginStyles.inputsWrapper}>
            <Controller
              name={name}
              control={control}
              rules={requiredRule}
              render={({ field }) => (
                <Select
                  label={<FormLabel label={label} />}
                  data={options}
                  value={field.value}
                  onChange={field.onChange}
                  searchable={false}
                  placeholder="Select an answer"
                  error={errors[name]?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </div>
        ))}
        <Button
          className={styles.submit}
          fullWidth
          type="submit"
          disabled={isSubmitting}
          withSpinner={isSubmitting}
        >
          Continue
        </Button>
      </form>
      <ul className={styles.buildSteps} aria-live="polite">
        {buildSteps.map(({ label, isDone }) => (
          <li
            key={label}
            className={isDone ? styles.buildStepDone : styles.buildStep}
          >
            {isDone ? (
              <Icon
                iconName="GreenCheckmarkIcon"
                width="1rem"
                height="1rem"
                className={styles.buildStepMarker}
              />
            ) : (
              <span className={styles.buildStepSpinner} aria-hidden />
            )}
            {label}
          </li>
        ))}
      </ul>
    </>
  );
};

export default OnboardingSurveyPage;
